import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import './PricingPage.css';
import './RateCard.css';
import './WebAppsPage.css';
import './LawFirmsPage.css';

// /law-firms — everything UBOTIKA can build for a law firm, in one place, for
// prospects to explore. noindex: it overlaps the indexed
// /law-firm-ai-intake-los-angeles landing page and is shared by link.
const ENTERPRISE_ENDPOINT = 'https://audit.ubotika.site/enterprise';
const DEMO_DISPLAY = '(424) 434-4541';
const DEMO_TEL = 'tel:+14244344541';

const JOURNEY = [
  'A prospective client calls',
  'Intake questions answered',
  'Conflict-check names captured',
  'Consultation booked',
  'Engagement letter drafted',
  'Signed and retainer paid',
  'Matter opened in your system',
];

const OFFERINGS = [
  {
    key: 'intake',
    tag: 'Voice agent',
    title: 'AI receptionist and intake, 24/7',
    body: 'Answers every call in English or Spanish, asks your intake questions, captures the parties’ names for your conflict check, and books the consultation. It never gives legal advice, and anything urgent goes straight to a person.',
    extra: 'demo',
  },
  {
    key: 'assistant',
    tag: 'Personal assistant',
    title: 'An assistant for each attorney',
    body: 'Triages the inbox, drafts replies in the attorney’s voice, summarizes long threads, and tracks the deadlines mentioned in email so nothing slips. The attorney approves everything that leaves.',
  },
  {
    key: 'templates',
    tag: 'Document automation',
    title: 'Your templates, filled in automatically',
    body: 'We turn your engagement letters, fee agreements, demand letters and standard forms into templates with fields. Intake answers fill them in, and an attorney reviews before anything is sent.',
  },
  {
    key: 'payments',
    tag: 'Engagement and payments',
    title: 'Signed and paid without the chasing',
    body: 'The engagement letter goes out for e-signature with the retainer request attached, with reminders until both are done. Payments run through a legal payment processor that keeps trust and operating funds separate. We connect it; we never hold client funds.',
  },
  {
    key: 'review',
    tag: 'Document review',
    title: 'Read the file, return the facts',
    body: 'Discovery, medical records, contracts: the system extracts key facts, builds a timeline, and flags inconsistencies, each pointing to the page it came from so the attorney can check it.',
  },
  {
    key: 'private-ai',
    tag: 'Private AI',
    title: 'AI that never leaves your office',
    body: 'For files that can’t go to a cloud service, the same tools run on a private model installed on hardware at your firm.',
    extra: 'localllm',
  },
  {
    key: 'portal',
    tag: 'Client portal',
    title: 'Fewer “any update?” calls',
    body: 'Clients see where their matter stands and upload documents to a secure portal instead of emailing attachments. Your staff updates one place.',
  },
  {
    key: 'website',
    tag: 'Website',
    title: 'A firm website that feeds intake',
    body: 'A fast, modern site for the firm whose contact forms flow into the same intake pipeline as the phone, so every inquiry lands in one queue.',
  },
];

const SAFEGUARDS = [
  {
    title: 'An attorney stays in the loop',
    body: 'Nothing AI-drafted reaches a client, a court or opposing counsel without a person approving it.',
  },
  {
    title: 'Client data is not training data',
    body: 'We use business-tier AI services that do not train on your data, or a private model on your own hardware when the matter calls for it.',
  },
  {
    title: 'Access follows your firm’s rules',
    body: 'Internal tools sit behind sign-in restricted to your firm’s accounts. We sign an NDA before seeing any system or file.',
  },
  {
    title: 'Built with bar guidance in mind',
    body: 'We design around the confidentiality, competence and supervision points in the State Bar of California’s generative-AI guidance and ABA Formal Opinion 512. We’re not a law firm: ethics decisions stay with yours.',
  },
];

const STEPS = [
  { title: 'A 30-minute walkthrough', body: 'You show us how calls, intake and paperwork move through the firm today.' },
  { title: 'One workflow, one fixed price', body: 'We pick the step that costs your attorneys the most time and quote it before any work starts.' },
  { title: 'Live in weeks, then extend', body: 'Your team uses something real early. Each next piece builds on what is already running.' },
];

export const FAQS = [
  {
    q: 'Will the AI give legal advice to callers?',
    a: 'No. It is set up to gather information, answer questions about the firm (hours, practice areas, how consultations work) and book time with an attorney. Questions that need legal judgment are routed to a person.',
  },
  {
    q: 'Does this work with our practice-management software?',
    a: 'Usually. Tools such as Clio and MyCase offer integrations we can connect to, so new matters, contacts and documents land where your team already works. We confirm what your system supports during the walkthrough.',
  },
  {
    q: 'Where do client files go?',
    a: 'Either to a business-tier AI service that does not train on your data, or nowhere outside your office if you choose the private, on-premise model. We tell you exactly which for each workflow before it is built.',
  },
  {
    q: 'What does it cost?',
    a: 'It depends on the workflow, so we quote a fixed price per piece after the walkthrough. You approve a number before any work starts.',
  },
];

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'AI systems for law firms — UBOTIKA',
    serviceType: 'AI automation for law firms',
    description: 'AI intake and receptionist, attorney assistants, document automation, engagement and payments, document review, private AI, client portals and websites for law firms.',
    provider: { '@type': 'LocalBusiness', '@id': `${SITE_URL}/#organization`, name: 'UBOTIKA', url: SITE_URL },
    areaServed: { '@type': 'City', name: 'Los Angeles' },
  },
];

function LawForm() {
  const [firm, setFirm] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [interests, setInterests] = useState([]);
  const [note, setNote] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const toggle = (tag) => setInterests((list) => (list.includes(tag) ? list.filter((t) => t !== tag) : [...list, tag]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (hp || status === 'sending') return;
    if (!firm.trim()) { setErrorMsg('Please enter your firm name.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErrorMsg('Please enter a valid email.'); return; }
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(ENTERPRISE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firm: firm.trim(),
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          role: 'Law firm',
          note: [interests.length ? `Interested in: ${interests.join(', ')}` : '', note.trim()].filter(Boolean).join('\n\n'),
          source: 'law-firms',
          landingPath: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      if (window.gtag) window.gtag('event', 'law_firm_lead', { source: 'law-firms' });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Could not send. Please email hello@ubotika.com directly.');
    }
  };

  if (status === 'success') {
    return (
      <div className="wa-form wa-form--done">
        <h3>Thank you.</h3>
        <p>We&apos;ll reply from hello@ubotika.com within one business day to set up the walkthrough.</p>
      </div>
    );
  }

  return (
    <form className="wa-form" onSubmit={onSubmit} noValidate>
      <h3>Book a 30-minute walkthrough</h3>
      <p className="wa-form-lede">Tick what caught your eye. No preparation needed.</p>
      <div className="wa-field">
        <label htmlFor="lf-firm">Firm</label>
        <input id="lf-firm" type="text" value={firm} onChange={(e) => setFirm(e.target.value)} required autoComplete="organization" />
      </div>
      <div className="wa-field-row">
        <div className="wa-field">
          <label htmlFor="lf-name">Your name</label>
          <input id="lf-name" type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </div>
        <div className="wa-field">
          <label htmlFor="lf-phone">Phone (optional)</label>
          <input id="lf-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
        </div>
      </div>
      <div className="wa-field">
        <label htmlFor="lf-email">Email</label>
        <input id="lf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
      </div>
      <fieldset className="lf-interests">
        <legend>Interested in</legend>
        {OFFERINGS.map((o) => (
          <label key={o.key} className={interests.includes(o.tag) ? 'is-on' : ''}>
            <input type="checkbox" checked={interests.includes(o.tag)} onChange={() => toggle(o.tag)} />
            {o.tag}
          </label>
        ))}
      </fieldset>
      <div className="wa-field">
        <label htmlFor="lf-note">Anything we should know?</label>
        <textarea id="lf-note" rows="3" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <input
        type="text" name="website_url_confirm" tabIndex="-1" autoComplete="off"
        aria-hidden="true" className="wa-hp" value={hp} onChange={(e) => setHp(e.target.value)}
      />
      {errorMsg && <p className="wa-form-error">{errorMsg}</p>}
      <button type="submit" className="cta-button wa-submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Request the walkthrough'}
      </button>
      <p className="wa-form-fine">
        NDA before anything confidential. Prefer email?{' '}
        <a href="mailto:hello@ubotika.com?subject=Law%20firm%20walkthrough">hello@ubotika.com</a>
      </p>
    </form>
  );
}

function LawFirmsPage() {
  return (
    <>
      <SEO
        title="AI for Law Firms: Intake, Documents, Payments | UBOTIKA"
        description="What UBOTIKA builds for law firms: 24/7 AI intake, attorney assistants, document automation, e-signed engagements with trust-safe payments, document review, private AI and client portals."
        path="/law-firms"
        schema={schema}
        noindex
      />
      <main id="main-content" className="pricing-page ratecard-page wa-page lf-page">
        <section className="pricing-hero">
          <p className="lf-eyebrow">For law firms</p>
          <h1>AI built around how a law firm actually runs.</h1>
          <p className="pricing-sub">
            From the first phone call to the signed engagement letter and the paid retainer:
            systems that take the admin off your attorneys&apos; desks, with an attorney
            approving anything that matters.
          </p>
        </section>

        <section className="ratecard-section">
          <h2>One matter, start to finish</h2>
          <p className="ratecard-lede">Each step below can run on its own, or connect into one pipeline.</p>
          <ol className="lf-journey">
            {JOURNEY.map((step, i) => (
              <li key={step}><span>{String(i + 1).padStart(2, '0')}</span>{step}</li>
            ))}
          </ol>
        </section>

        <section className="ratecard-section">
          <h2>What we can build for your firm</h2>
          <div className="wa-grid">
            {OFFERINGS.map((o) => (
              <article className="wa-card" key={o.key}>
                <p className="wa-card-tag">{o.tag}</p>
                <h3>{o.title}</h3>
                <p className="wa-card-body">{o.body}</p>
                {o.extra === 'demo' && (
                  <p className="lf-card-link">Hear it now: <a href={DEMO_TEL}>call {DEMO_DISPLAY}</a></p>
                )}
                {o.extra === 'localllm' && (
                  <p className="lf-card-link"><Link to="/localllm">How the private model works</Link></p>
                )}
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-section">
          <h2>Confidentiality comes first</h2>
          <div className="ratecard-grid ratecard-grid--4">
            {SAFEGUARDS.map((s) => (
              <article className="ratecard-vertical" key={s.title}>
                <h3>{s.title}</h3>
                <p className="wa-card-body">{s.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-section">
          <h2>How we start</h2>
          <ol className="lf-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="lf-step-num">{i + 1}</span>
                <div><h3>{s.title}</h3><p>{s.body}</p></div>
              </li>
            ))}
          </ol>
        </section>

        <section className="ratecard-section">
          <h2>Questions partners ask</h2>
          <div className="pricing-faq-list">
            {FAQS.map((f) => (
              <article className="pricing-faq-item" key={f.q}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-pilot wa-cta" id="walkthrough">
          <div>
            <h2>See it on your own workflow.</h2>
            <p>
              Thirty minutes, no slides. Show us how a new client reaches you today and we&apos;ll
              show you which step is worth automating first — and which isn&apos;t.
            </p>
            <ul className="wa-points">
              <li>Fixed price per piece, approved before work starts</li>
              <li>You own the source and the data</li>
              <li>NDA before anything confidential</li>
            </ul>
          </div>
          <LawForm />
        </section>
        <Footer />
      </main>
    </>
  );
}

export default LawFirmsPage;
